import { ROLE_LABELS, timeAgo, formatDate, actionLabel } from './shared.js';

export default function UserDetailPanel({
  selectedDetail, currentUser, isAdmin, theme, onClose,
  activeTab, setActiveTab,
  activityLog, activityLoading,
  onRoleChange, onDeactivate, onReactivate,
  onEdit, onResetPassword,
}) {
  if (!selectedDetail) {
    return (
      <aside style={{
        width: 360, borderLeft: `2px solid ${theme.text}`, padding: 20,
        background: theme.bgElevated, height: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: theme.textFaint, fontSize: 12 }}>user not found</span>
      </aside>
    );
  }

  const canManage = isAdmin && selectedDetail.id !== currentUser?.id;

  return (
    <aside style={{
      width: 360, borderLeft: `2px solid ${theme.text}`,
      padding: 20, overflowY: 'auto',
      background: theme.bgElevated,
      height: '100%', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 12, paddingBottom: 12, borderBottom: `2px solid ${theme.text}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, border: `1px solid ${theme.borderMedium}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, letterSpacing: '0.06em', color: theme.text, flexShrink: 0,
          }}>{selectedDetail.initials || '??'}</div>
          <div>
            <h3 style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 18, margin: 0, color: theme.text }}>
              {selectedDetail.name}
              {currentUser?.id === selectedDetail.id && <span style={{ fontSize: 11, color: theme.textFaint, marginLeft: 6 }}>(you)</span>}
            </h3>
            <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 2 }}>{selectedDetail.email}</div>
          </div>
        </div>
        <span onClick={onClose} style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}>✕</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <span style={{
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: selectedDetail.role === 'admin' ? theme.accent : theme.text,
          border: `1px solid ${selectedDetail.role === 'admin' ? theme.accent : theme.border}`,
          padding: '3px 8px',
        }}>{ROLE_LABELS[selectedDetail.role] || selectedDetail.role}</span>
        {!selectedDetail.is_active && (
          <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint, border: `1px solid ${theme.border}`, padding: '3px 8px' }}>inactive</span>
        )}
      </div>

      {canManage && (
        <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
          <span onClick={() => onEdit(selectedDetail)} style={{ fontSize: 13, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase' }}>edit profile</span>
          <span onClick={() => onResetPassword(selectedDetail.id)} style={{ fontSize: 13, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase' }}>reset password</span>
        </div>
      )}

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 14, marginTop: 16, marginBottom: 16,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        {['overview', 'activity'].map(tab => (
          <span
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              fontSize: 12, textTransform: 'lowercase',
              paddingBottom: 8, marginBottom: -1, cursor: 'pointer',
              color: activeTab === tab ? theme.text : theme.textTertiary,
              borderBottom: `2px solid ${activeTab === tab ? theme.accent : 'transparent'}`,
            }}
          >{tab}</span>
        ))}
      </div>

      {activeTab === 'overview' ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: theme.border, marginBottom: 20 }}>
            <StatCard label="uploaded" value={selectedDetail.fonts_uploaded || 0} theme={theme} />
            <StatCard label="active" value={selectedDetail.active_fonts || 0} theme={theme} />
            <StatCard label="last login" value={timeAgo(selectedDetail.last_login_at)} theme={theme} isText />
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 10, fontWeight: 600, color: theme.textFaint,
              marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
              paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
            }}>details</div>
            <InfoRow label="email" value={selectedDetail.email} theme={theme} />
            <InfoRow label="joined" value={formatDate(selectedDetail.created_at)} theme={theme} />
            <InfoRow label="last login" value={formatDate(selectedDetail.last_login_at)} theme={theme} />
            <InfoRow label="status" value={selectedDetail.is_active ? 'active' : 'inactive'} theme={theme} />
          </div>

          {canManage && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: theme.textFaint,
                marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
                paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
              }}>role</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {['admin', 'editor', 'viewer'].map(role => (
                  <button
                    key={role}
                    onClick={() => onRoleChange(selectedDetail.id, role)}
                    style={{
                      flex: 1, padding: '8px 6px', fontSize: 11, textTransform: 'lowercase',
                      background: 'none', borderRadius: 0,
                      border: `1px solid ${selectedDetail.role === role ? theme.accent : theme.borderMedium}`,
                      color: selectedDetail.role === role ? theme.accent : theme.textMuted,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >{ROLE_LABELS[role]}</button>
                ))}
              </div>
            </div>
          )}

          {canManage && (
            <div style={{ border: `1px solid ${theme.redBorder}`, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: theme.red, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>danger zone</div>
              {selectedDetail.is_active ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontSize: 12, color: theme.textSecondary }}>deactivate — user can no longer log in</div>
                  <span onClick={() => onDeactivate(selectedDetail.id, selectedDetail.name)} style={{ fontSize: 12, color: theme.red, cursor: 'pointer', textTransform: 'lowercase', flexShrink: 0 }}>deactivate</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontSize: 12, color: theme.textSecondary }}>reactivate — restore access</div>
                  <span onClick={() => onReactivate(selectedDetail.id, selectedDetail.name)} style={{ fontSize: 12, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase', flexShrink: 0 }}>reactivate</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          {activityLoading ? (
            <div style={{ textAlign: 'center', padding: 20, color: theme.textMuted, fontSize: 12 }}>loading activity…</div>
          ) : activityLog.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint, fontSize: 12 }}>no activity recorded</div>
          ) : (
            activityLog.map((a, i) => (
              <div key={a.id || i} style={{
                padding: '10px 0', borderBottom: `1px solid ${theme.borderLight}`,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ width: 6, height: 6, background: theme.accent, marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: theme.text }}>{actionLabel(a.action)}</div>
                  {a.details && (
                    <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 2 }}>
                      {typeof a.details === 'object'
                        ? Object.entries(a.details).map(([k, v]) => `${k}: ${v}`).join(' · ')
                        : String(a.details)}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: theme.textGhost, marginTop: 4 }}>{timeAgo(a.created_at)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </aside>
  );
}

function StatCard({ label, value, theme, isText = false }) {
  return (
    <div style={{ padding: 14, background: theme.bgElevated }}>
      <div style={{ fontSize: 9, color: theme.textFaint, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: isText ? 13 : 20, color: theme.text }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value, theme }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontSize: 12, padding: '7px 0',
      borderBottom: `1px solid ${theme.borderLight}`,
    }}>
      <span style={{ color: theme.textFaint, textTransform: 'lowercase' }}>{label}</span>
      <span style={{ color: theme.textSecondary }}>{value || '—'}</span>
    </div>
  );
}
